import { Issue } from "generated-graphql/graphql";
import { DueDateWarningsByUserId } from "hooks/useSubmissionForm";
import React, { createContext, useContext, ReactNode, useMemo } from "react";

interface SubmissionFormContextType {
  issues: Issue[];
  dueDateWarningsByUserId: DueDateWarningsByUserId;
}

const SubmissionFormContext = createContext<SubmissionFormContextType | undefined>(undefined);

// Stable so providers that omit the prop don't rebuild the context value every render.
const EMPTY_WARNINGS: DueDateWarningsByUserId = {};

interface SubmissionFormProviderProps {
  children: ReactNode;
  issues: Issue[];
  dueDateWarningsByUserId?: DueDateWarningsByUserId;
}

export function SubmissionFormProvider({
  children,
  issues,
  dueDateWarningsByUserId = EMPTY_WARNINGS,
}: SubmissionFormProviderProps) {
  const value = useMemo(
    () => ({ issues, dueDateWarningsByUserId }),
    [issues, dueDateWarningsByUserId]
  );

  return <SubmissionFormContext.Provider value={value}>{children}</SubmissionFormContext.Provider>;
}

export function useSubmissionFormContext() {
  const context = useContext(SubmissionFormContext);
  if (context === undefined) {
    throw new Error("useSubmissionFormContext must be used within a SubmissionFormProvider");
  }
  return context;
}

export function useReviewersIssues() {
  const { issues } = useSubmissionFormContext();
  return useMemo(
    () => issues.filter((i) => i.modelName === "Submission" && i.key === "reviewers"),
    [issues]
  );
}

export function useReviewerIssues() {
  const { issues } = useSubmissionFormContext();
  return useMemo(() => issues.filter((i) => i.modelName === "SubmissionReviewer"), [issues]);
}

export function useDueDateWarningsByUserId(): DueDateWarningsByUserId {
  const { dueDateWarningsByUserId } = useSubmissionFormContext();
  return dueDateWarningsByUserId;
}
